import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Stack } from "@mui/material";
import { useIntercom } from "react-use-intercom";

import SkeletonLoader from "../../Components/Skeleton/skeleton-loader.js";
import SegmentControl from "../../Components/Buttons/segment-control";
import PrimaryButton from "../../Components/Buttons/primary-button.js";
import OutlineButton from "../../Components/Buttons/outline-button.js";

import renderVerifyToViewVirtualCard from "../../Organisms/Modals/Virtual Card/verify-to-view-virtual-card.js";
import renderRequestPhysicalCard from "../../Organisms/Modals/Physical Card/request-physical-card.js";

import { useModalContext } from "../../Contexts/ModalContext.js";

import { dispatchErrorToast } from "../../Utils/helpers.js";

import { ArrowLeftCircle, Eye, EyeSlash } from "../../Assets/Icons/index.js";

import "./manage-cards.scss";

const sectionTitleLookUp = { banking: "Manage Cards", deals: "Settings" };

const cardTypes = [
  {
    label: "Virtual Card",
    value: "virtual",
  },
  {
    label: "Physical Card",
    value: "physical",
  },
];

function UnitCardSkeleton() {
  return (
    <Stack margin="0 auto" maxWidth="350px" flexDirection="column" gap="12px">
      <Stack flexDirection="row" alignItems="center" justifyContent="space-between" className="row-gap-3 h-100 w-100">
        <SkeletonLoader className="border--rounded-lg" width="200px" height="20px" />
        <SkeletonLoader className="border--rounded-full" width="28px" height="28px" />
      </Stack>
      <SkeletonLoader className="border--rounded-sm" width="100%" height="220px" />
    </Stack>
  );
}

export default function ManageCards() {
  const [cardToRender, setCardToRender] = useState("virtual");

  const [loadingUnitCard, setLoadingUnitCard] = useState(true);

  const [showVirtualCardSensitiveData, setShowVirtualCardSensitiveData] = useState(false);
  const [showPhysicalCardSensitiveData, setShowPhysicalCardSensitiveData] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { show: showIntercom } = useIntercom();

  const modalContext = useModalContext();

  const { card_id, physical_card_id, customer_token } = location?.state ?? {};

  useEffect(() => {
    const onUnitLoad = function (e) {
      if (e.detail.errors && e.detail.errors[0].status === "401") {
        dispatchErrorToast("Session expired. Please verify your phone number again.", { key: "request_error" });
        renderVerifyToViewVirtualCard(modalContext);
        navigate(-1);
      }
    };
    document.querySelector("unit-elements-card")?.addEventListener("unitOnLoad", onUnitLoad);

    return () => document.querySelector("unit-elements-card")?.removeEventListener("unitOnLoad", onUnitLoad);
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    const onUnitCardLoad = () => setLoadingUnitCard(false);
    document.querySelector("unit-elements-card")?.addEventListener("unitOnLoad", onUnitCardLoad);

    return () => document.querySelector("unit-elements-card")?.removeEventListener("unitOnLoad", onUnitCardLoad);
  }, []);

  useEffect(() => {
    if ((!card_id && !physical_card_id) || !customer_token) {
      navigate(-1);
    }
  }, [card_id, physical_card_id, customer_token, navigate]);

  const unitVirtualCardComponentTargetId = `unit-elements-card[card-id='${card_id}']`;
  const unitPhysicalCardComponentTargetId = `unit-elements-card[card-id='${physical_card_id}']`;

  const onVCShowDataButtonClick = () => {
    document
      .querySelector(unitVirtualCardComponentTargetId)
      .dispatchEvent(
        new CustomEvent(showVirtualCardSensitiveData ? "unitRequestHideSensitiveData" : "unitRequestShowSensitiveData")
      );
    setShowVirtualCardSensitiveData(showVirtualCardSensitiveData ? false : true);
  };

  const onPCShowDataButtonClick = () => {
    document
      .querySelector(unitPhysicalCardComponentTargetId)
      .dispatchEvent(
        new CustomEvent(showPhysicalCardSensitiveData ? "unitRequestHideSensitiveData" : "unitRequestShowSensitiveData")
      );
    setShowPhysicalCardSensitiveData(showPhysicalCardSensitiveData ? false : true);
  };

  const VCShowDataIcon = showVirtualCardSensitiveData ? EyeSlash : Eye;
  const PCShowDataIcon = showPhysicalCardSensitiveData ? EyeSlash : Eye;

  return (
    <div className="manage-cards">
      <section className="card-section-wrapper">
        <div className="max-width-container">
          <div className="card-section">
            <div className="section-header">
              <button className="go-back-button" onClick={() => navigate(-1)}>
                <ArrowLeftCircle />
              </button>
              <h3 className="section-title">{sectionTitleLookUp[location.pathname.split("/")[1]]}</h3>
              <div className="dummy"></div>
            </div>
            <div className="content">
              <div className="top-container d-flex flex-column">
                {!!physical_card_id && (
                  <SegmentControl
                    className="mb-5"
                    value={cardToRender}
                    onChange={(_, value) => setCardToRender(value)}
                    exclusive
                    segmentOptions={cardTypes}
                  />
                )}
                <Stack className="cards-container">
                  {cardToRender === "virtual" && (
                    <>
                      <div className="card-container">
                        {loadingUnitCard && <UnitCardSkeleton />}
                        <div className="card-wrapper" data-loading={loadingUnitCard}>
                          <unit-elements-card
                            card-id={card_id}
                            customer-token={customer_token}
                            hide-sensitive-data-button="true"
                            theme="https://ui.s.unit.sh/resources/480/themes/040be43b-5e1e-4dce-b370-387ffb427487.json"
                          ></unit-elements-card>
                        </div>
                        <button
                          id="virtual-card-show-data-button"
                          className="show-sensitive-data-button"
                          onClick={onVCShowDataButtonClick}
                        >
                          {showVirtualCardSensitiveData ? "Hide" : "Show"} Card Data
                          <span className="icon-wrapper">
                            <VCShowDataIcon className="eye-icon" />
                          </span>
                        </button>
                      </div>
                      {!physical_card_id && (
                        <PrimaryButton className="mt-4" onClick={renderRequestPhysicalCard.bind(this, modalContext)}>
                          Request physical card
                        </PrimaryButton>
                      )}
                    </>
                  )}
                  {cardToRender === "physical" && (
                    <div className="card-container">
                      {loadingUnitCard && <UnitCardSkeleton />}
                      <div className="card-wrapper" data-loading={loadingUnitCard}>
                        <unit-elements-card
                          card-id={physical_card_id}
                          customer-token={customer_token}
                          hide-sensitive-data-button="true"
                          theme="https://ui.s.unit.sh/resources/480/themes/040be43b-5e1e-4dce-b370-387ffb427487.json"
                        ></unit-elements-card>
                      </div>
                      <button
                        id="physical-card-show-data-button"
                        className="show-sensitive-data-button"
                        onClick={onPCShowDataButtonClick}
                      >
                        {showPhysicalCardSensitiveData ? "Hide" : "Show"} Card Data
                        <span className="icon-wrapper">
                          <PCShowDataIcon className="eye-icon" />
                        </span>
                      </button>
                    </div>
                  )}
                </Stack>
              </div>
              <div className="bottom-container">
                <OutlineButton className="green-theme" onClick={showIntercom}>
                  Need to close Stake Checking?<br></br>Contact Support
                </OutlineButton>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
